import { Anchor, Button, Flex, Input, Radio, RadioGroup, Switch, Text } from '@byecode/ui'
import { ErrorMessage, findWechatPaymentAccount, TagIcon, useAtomAction, useAtomData, wechatPaymentAccountLabel } from '@lighthouse/shared'
import React, { useCallback, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useAsyncFn } from 'react-use'

import { fetchOtherPlatformsAtom } from '@/atoms/application/action'
import { otherPlatformListAtom } from '@/atoms/application/state'
import { IntegratedCard } from '@/components/IntegratedCard'
import {
    otherPlatformBackgroundColorMap,
    otherPlatformColorMap,
    otherPlatformDescribeMap,
    otherPlatformNameMap
} from '@/containers/OtherPlatforms/constant'
import * as srv from '@/services'

import { PLACEHOLDER_PASSWORD } from '../constants'
import * as SC from '../styles'
import { buildWechatPaymentAccountUpdate } from './form'
import type { WeChatPayAccountFormState } from './type'

interface WeChatPayAccountProps {
    onClose?: () => void
}

const initData: WeChatPayAccountFormState = {
    id: '',
    displayName: '',
    companyName: '',
    enabled: true,
    appId: '',
    mchId: '',
    appSecret: '',
    apiV3Key: '',
    mchPrivateKey: '',
    mchSerialNo: '',
    publicKey: '',
    publicKeyId: '',
    mode: 'PRIVATE_KEY'
}

const WeChatPayAccountForm: React.FunctionComponent<WeChatPayAccountProps & { accountId: string }> = ({ onClose, accountId }) => {
    const otherPlatforms = useAtomData(otherPlatformListAtom)
    const { run: fetchOtherPlatforms } = useAtomAction(fetchOtherPlatformsAtom)

    const account: WeChatPayAccountFormState = useMemo(() => {
        const otherPlatform = findWechatPaymentAccount(otherPlatforms, accountId)
        return otherPlatform?.configurationComplete && otherPlatform.type === 'WECHAT_PAY'
            ? {
                  ...initData,
                  mode: 'PRIVATE_KEY',
                  ...otherPlatform?.config,
                  id: otherPlatform.id,
                  appSecret: PLACEHOLDER_PASSWORD,
                  apiV3Key: PLACEHOLDER_PASSWORD,
                  mchSerialNo: PLACEHOLDER_PASSWORD,
                  mchPrivateKey: PLACEHOLDER_PASSWORD,
                  publicKey: otherPlatform.config.mode === 'PUBLIC_KEY' ? PLACEHOLDER_PASSWORD : '',
                  publicKeyId: otherPlatform.config.mode === 'PUBLIC_KEY' ? PLACEHOLDER_PASSWORD : ''
              }
            : { ...initData, ...otherPlatform?.config, id: accountId }
    }, [otherPlatforms, accountId])

    const {
        control,
        register,
        handleSubmit,
        watch,
        formState: { errors }
    } = useForm({
        mode: 'onSubmit',
        defaultValues: account
    })

    const payMode = watch('mode')

    const [{ loading: submitLoading }, doUpdateIntegration] = useAsyncFn(srv.updateIntegration)
    const handleUpdate = useCallback(
        async (params: WeChatPayAccountFormState) => {
            const { id } = params
            if (!id || submitLoading) {
                return
            }
            const res = await doUpdateIntegration({
                type: 'WECHAT_PAY',
                id,
                config: buildWechatPaymentAccountUpdate(params)
            })
            if (res) {
                await fetchOtherPlatforms()
                onClose?.()
            }
        },
        [doUpdateIntegration, fetchOtherPlatforms, onClose, submitLoading]
    )

    const handleInviteSubmit = useCallback(
        (ev: React.FormEvent) => {
            ev.preventDefault()
            handleSubmit(handleUpdate)()
        },
        [handleSubmit, handleUpdate]
    )

    return (
        <SC.Container onSubmit={handleInviteSubmit}>
            <SC.Content>
                <TagIcon
                    size={48}
                    iconSize={32}
                    icon="WechatPay"
                    radius={6}
                    iconColor={otherPlatformColorMap['WECHAT_PAY']}
                    background={otherPlatformBackgroundColorMap['WECHAT_PAY']}
                />
                <SC.Title>{otherPlatformNameMap['WECHAT_PAY']}</SC.Title>
                <SC.Item>
                    <SC.Description>{otherPlatformDescribeMap['WECHAT_PAY']}</SC.Description>
                </SC.Item>
                <SC.Tip>
                    <SC.Icon color="var(--color-main)" type="WarningCircle" />
                    <SC.Text>如果您完善授权时遇到问题，请查看帮助文档：</SC.Text>
                    <Anchor
                        target="_blank"
                        style={{ fontWeight: 600, color: 'var(--color-main)' }}
                        to="https://byecodehelp.yuque.com/org-wiki-byecodehelp-zavfcl/oevich/pxnbuk2ax3qqbe25"
                    >
                        微信商户号授权配置
                    </Anchor>
                </SC.Tip>

                <SC.Item style={{ marginTop: 20 }}>
                    <SC.Text>配置名称 *</SC.Text>
                    <ErrorMessage name="displayName" errors={errors}>
                        <Input
                            placeholder="例如：上海门店收款"
                            {...register('displayName', { validate: v => !!v?.trim() || '请填写配置名称' })}
                        />
                    </ErrorMessage>
                </SC.Item>
                <SC.Item>
                    <SC.Text>所属公司 *</SC.Text>
                    <ErrorMessage name="companyName" errors={errors}>
                        <Input
                            placeholder="填写商户号所属公司"
                            {...register('companyName', { validate: v => !!v?.trim() || '请填写所属公司' })}
                        />
                    </ErrorMessage>
                </SC.Item>
                <SC.Item>
                    <Flex alignItems="center" justifyContent="space-between">
                        <SC.Text>允许新收款</SC.Text>
                        <Controller
                            name="enabled"
                            control={control}
                            render={({ field }) => (
                                <Switch checked={field.value !== false} onChange={event => field.onChange(event.currentTarget.checked)} />
                            )}
                        />
                    </Flex>
                    <SC.Description>关闭后停止创建新支付和维护分账接收方，历史交易的退款、查询及回调仍可处理。</SC.Description>
                </SC.Item>
                <SC.Item style={{ marginTop: 20 }}>
                    <SC.Text>微信公众号</SC.Text>
                    <IntegratedCard type="WECHAT_OFFICIAL_ACCOUNT" />
                    <SC.Description>
                        如果仅在小程序中使用微信支付，则不需完善该配置；如果在网页应用中使用微信支付，则需要完善该配置。
                    </SC.Description>
                </SC.Item>
                <SC.Item>
                    <SC.Text>
                        微信支付商户号
                        <SC.Text color="red" style={{ paddingLeft: 4 }}>
                            *
                        </SC.Text>
                    </SC.Text>
                    <ErrorMessage name="mchId" errors={errors}>
                        <Input
                            placeholder="请填入微信支付商户号"
                            readOnly={!!account.mchId}
                            autoComplete="new-password"
                            {...register('mchId', { required: '不能为空' })}
                        />
                    </ErrorMessage>
                    {account.mchId && <SC.Description>商户号不可更换；更换收款主体请新增商户配置。</SC.Description>}
                    <SC.Description>
                        需先登录
                        <Anchor
                            target="_blank"
                            style={{ color: 'var(--color-main)' }}
                            to="https://pay.weixin.qq.com/index.php/core/home/login?return_url=%2F"
                        >
                            「商户平台」
                        </Anchor>
                        {'>「产品中心」>「开发配置」页面找到对应的商户号，也就是商户在微信侧申请入驻的收款账号'}
                    </SC.Description>
                </SC.Item>
                <SC.Item>
                    <SC.Text>
                        API v3 密钥
                        <SC.Text color="red" style={{ paddingLeft: 4 }}>
                            *
                        </SC.Text>
                    </SC.Text>
                    <ErrorMessage name="apiV3Key" errors={errors}>
                        <Controller
                            name="apiV3Key"
                            control={control}
                            rules={{ required: '不能为空' }}
                            render={({ field }) => {
                                return (
                                    <Input
                                        autoComplete="new-password"
                                        type="password"
                                        placeholder="请填入 API v3 密钥 "
                                        onFocus={() => {
                                            if (field.value === PLACEHOLDER_PASSWORD) {
                                                field.onChange('')
                                            }
                                        }}
                                        onBlur={() => {
                                            if (!field.value.trim() && account.apiV3Key === PLACEHOLDER_PASSWORD) {
                                                field.onChange(PLACEHOLDER_PASSWORD)
                                            }
                                        }}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                )
                            }}
                        />
                    </ErrorMessage>
                    <SC.Description>{'需先登录「商户平台」>「账户中心」>「账户设置」>「API安全」生成该密钥'}</SC.Description>
                </SC.Item>
                <SC.Item>
                    <SC.Text>
                        商户证书私钥
                        <SC.Text color="red" style={{ paddingLeft: 4 }}>
                            *
                        </SC.Text>
                    </SC.Text>
                    <ErrorMessage name="mchPrivateKey" errors={errors}>
                        <Controller
                            name="mchPrivateKey"
                            control={control}
                            rules={{ required: '不能为空' }}
                            render={({ field }) => {
                                return (
                                    <Input
                                        autoComplete="new-password"
                                        type="password"
                                        placeholder="请填入商户证书私钥"
                                        onFocus={() => {
                                            if (field.value === PLACEHOLDER_PASSWORD) {
                                                field.onChange('')
                                            }
                                        }}
                                        onBlur={() => {
                                            if (!field.value.trim() && account.mchPrivateKey === PLACEHOLDER_PASSWORD) {
                                                field.onChange(PLACEHOLDER_PASSWORD)
                                            }
                                        }}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                )
                            }}
                        />
                    </ErrorMessage>
                    <SC.Description>
                        {'需先登录「商户平台」>「账户中心」>「账户设置」>「API安全」申请API证书，获取私钥，'}
                        <Anchor
                            target="_blank"
                            style={{ color: 'var(--color-main)' }}
                            to="https://pay.weixin.qq.com/doc/v3/merchant/4013053053"
                        >
                            参考文档
                        </Anchor>
                    </SC.Description>
                </SC.Item>
                <SC.Item>
                    <SC.Text>
                        商户证书序列号
                        <SC.Text color="red" style={{ paddingLeft: 4 }}>
                            *
                        </SC.Text>
                    </SC.Text>
                    <ErrorMessage name="mchSerialNo" errors={errors}>
                        <Controller
                            name="mchSerialNo"
                            control={control}
                            rules={{ required: '不能为空' }}
                            render={({ field }) => {
                                return (
                                    <Input
                                        autoComplete="new-password"
                                        type="password"
                                        placeholder="请填入商户证书序列号"
                                        onFocus={() => {
                                            if (field.value === PLACEHOLDER_PASSWORD) {
                                                field.onChange('')
                                            }
                                        }}
                                        onBlur={() => {
                                            if (!field.value.trim() && account.mchSerialNo === PLACEHOLDER_PASSWORD) {
                                                field.onChange(PLACEHOLDER_PASSWORD)
                                            }
                                        }}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                )
                            }}
                        />
                    </ErrorMessage>
                    <SC.Description>
                        {'需先登录「商户平台」>「账户中心」>「账户设置」>「API安全」>「API证书」>「查看证书」查看商户API证书序列号'}
                    </SC.Description>
                </SC.Item>
                <SC.Item>
                    <SC.Text>支付模式</SC.Text>
                    <ErrorMessage name="mode" errors={errors}>
                        <Controller
                            name="mode"
                            control={control}
                            render={({ field }) => {
                                return (
                                    <RadioGroup
                                        name="mode"
                                        value={field.value}
                                        onChange={v => {
                                            field.onChange(v)
                                        }}
                                        style={{ gap: 24 }}
                                    >
                                        <Radio size="xs" value="PUBLIC_KEY" label="公钥模式" />
                                        <Radio size="xs" value="PRIVATE_KEY" label="私钥模式" />
                                    </RadioGroup>
                                )
                            }}
                        />
                    </ErrorMessage>
                    <SC.Description>
                        <p>什么情况下使用公钥模式？</p>
                        <p>情况一：如果你是个新公司，新申请了一个商户号，此前也从未对接过微信支付，请使用公钥模式。</p>
                        <p>情况二：如果您此前已经使用过平台证书模式，然后证书过期导致无法正常支付，请使用公钥模式。</p>
                    </SC.Description>
                </SC.Item>
                {payMode === 'PUBLIC_KEY' && (
                    <>
                        <SC.Item>
                            <SC.Text>
                                公钥ID
                                <SC.Text color="red" style={{ paddingLeft: 4 }}>
                                    *
                                </SC.Text>
                            </SC.Text>
                            <ErrorMessage name="publicKeyId" errors={errors}>
                                <Controller
                                    name="publicKeyId"
                                    control={control}
                                    rules={{ required: '不能为空' }}
                                    render={({ field }) => {
                                        return (
                                            <Input
                                                autoComplete="new-password"
                                                type="password"
                                                placeholder="请填入公钥ID"
                                                onFocus={() => {
                                                    if (field.value === PLACEHOLDER_PASSWORD) {
                                                        field.onChange('')
                                                    }
                                                }}
                                                onBlur={() => {
                                                    if (!field.value.trim() && account.publicKeyId === PLACEHOLDER_PASSWORD) {
                                                        field.onChange(PLACEHOLDER_PASSWORD)
                                                    }
                                                }}
                                                value={field.value}
                                                onChange={field.onChange}
                                            />
                                        )
                                    }}
                                />
                            </ErrorMessage>
                            <SC.Description>
                                需先登录
                                <Anchor target="_blank" to="https://pay.weixin.qq.com/index.php/core/home/login?return_url=%2F">
                                    「商户平台」
                                </Anchor>
                                {'>「账户中心」>「账户设置」>「API安全」>「微信支付公钥」可下载公钥后可看到公钥ID，'}
                                <Anchor target="_blank" to="https://pay.weixin.qq.com/doc/v3/merchant/4012153196">
                                    参考文档
                                </Anchor>
                            </SC.Description>
                        </SC.Item>
                        <SC.Item>
                            <SC.Text>
                                公钥
                                <SC.Text color="red" style={{ paddingLeft: 4 }}>
                                    *
                                </SC.Text>
                            </SC.Text>
                            <ErrorMessage name="publicKey" errors={errors}>
                                <Controller
                                    name="publicKey"
                                    control={control}
                                    rules={{ required: '不能为空' }}
                                    render={({ field }) => {
                                        return (
                                            <Input
                                                autoComplete="new-password"
                                                type="password"
                                                placeholder="请填入公钥"
                                                onFocus={() => {
                                                    if (field.value === PLACEHOLDER_PASSWORD) {
                                                        field.onChange('')
                                                    }
                                                }}
                                                onBlur={() => {
                                                    if (!field.value.trim() && account.publicKey === PLACEHOLDER_PASSWORD) {
                                                        field.onChange(PLACEHOLDER_PASSWORD)
                                                    }
                                                }}
                                                value={field.value}
                                                onChange={field.onChange}
                                            />
                                        )
                                    }}
                                />
                            </ErrorMessage>
                            <SC.Description>
                                需先登录
                                <Anchor target="_blank" to="https://pay.weixin.qq.com/index.php/core/home/login?return_url=%2F">
                                    「商户平台」
                                </Anchor>
                                {'>「账户中心」>「账户设置」>「API安全」>「微信支付公钥」可下载公钥，'}
                                <Anchor target="_blank" to="https://pay.weixin.qq.com/doc/v3/merchant/4012153196">
                                    参考文档
                                </Anchor>
                            </SC.Description>
                        </SC.Item>
                    </>
                )}
            </SC.Content>
            <SC.Footer>
                <SC.LeftFill />
                <SC.RightFill>
                    <Button size="lg" style={{ width: 104 }} onClick={() => onClose?.()}>
                        取消
                    </Button>
                    <Button size="lg" style={{ width: 104 }} type="primary" htmlType="submit" disabled={submitLoading}>
                        确定
                    </Button>
                </SC.RightFill>
            </SC.Footer>
        </SC.Container>
    )
}

const WeChatPayAccount: React.FunctionComponent<WeChatPayAccountProps> = ({ onClose }) => {
    const otherPlatforms = useAtomData(otherPlatformListAtom)
    const { run: fetchOtherPlatforms } = useAtomAction(fetchOtherPlatformsAtom)
    const [selectedId, setSelectedId] = useState<string>()
    const [creating, setCreating] = useState(false)
    const accounts = (otherPlatforms ?? []).filter(item => item.type === 'WECHAT_PAY')

    const addAccount = async () => {
        if (creating) return
        setCreating(true)
        try {
            const id = await srv.createWechatPaymentAccount()
            if (id) {
                await fetchOtherPlatforms()
                setSelectedId(id)
            }
        } finally {
            setCreating(false)
        }
    }

    if (selectedId) {
        return <WeChatPayAccountForm key={selectedId} accountId={selectedId} onClose={() => setSelectedId(undefined)} />
    }

    return (
        <SC.Container onSubmit={event => event.preventDefault()}>
            <SC.Content>
                <SC.Title>微信商户号管理</SC.Title>
                <SC.Description>每个支付节点手动选择商户。历史交易始终归属于原收款商户。</SC.Description>
                <Flex justifyContent="flex-end" style={{ marginTop: 16 }}>
                    <Button type="primary" disabled={creating} onClick={addAccount}>
                        新增商户
                    </Button>
                </Flex>
                {accounts.length === 0 && <Text style={{ padding: '24px 0' }}>暂未添加商户，请先新增商户配置。</Text>}
                {accounts.map(account => (
                    <Flex
                        key={account.id}
                        alignItems="center"
                        justifyContent="space-between"
                        gap={12}
                        style={{ marginTop: 12, padding: 16, background: 'var(--color-gray-50)', borderRadius: 6 }}
                    >
                        <Flex direction="column" gap={8}>
                            <Text>{wechatPaymentAccountLabel(account.config)}</Text>
                            <Text size={12} color="var(--color-gray-500)">
                                {account.configurationComplete ? '已完善' : '待完善'}
                                {account.config?.enabled === false ? ' · 已停止新收款' : ' · 允许新收款'}
                            </Text>
                        </Flex>
                        <Button onClick={() => setSelectedId(account.id)}>配置</Button>
                    </Flex>
                ))}
                <SC.Description style={{ marginTop: 20 }}>
                    商户配置不提供删除；不再使用时可关闭新收款，保留历史交易处理能力。
                </SC.Description>
            </SC.Content>
            <SC.Footer>
                <SC.LeftFill />
                <SC.RightFill>
                    <Button size="lg" onClick={onClose}>
                        关闭
                    </Button>
                </SC.RightFill>
            </SC.Footer>
        </SC.Container>
    )
}

export default WeChatPayAccount
